import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Seo, { buildBreadcrumbs } from "@/components/site/Seo";
import { ArrowUpRight, MapPin, CheckCircle2, Phone, MessageCircle } from "lucide-react";
import { CLIENT_PROJECTS, UK_HERO_IMG } from "@/data/projects";
import { AREAS } from "@/data/areas";
import { SERVICES } from "@/data/services";
import FaqSection, { faqJsonLd } from "@/components/site/FaqSection";

const PHONE_TEL = "tel:+447481626260";
const WA = (m) => `https://wa.me/447481626260?text=${encodeURIComponent(m)}`;

export const AREA_SLUGS = Object.keys(AREAS);

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS[slug];
  if (!area) return <Navigate to="/" replace />;

  const localProjects = CLIENT_PROJECTS.filter((p) =>
    p.location.toLowerCase().includes(area.name.toLowerCase())
  );

  const linkedServices = (area.linkedServices || []).map((s) => SERVICES[s]).filter(Boolean);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Residential Construction & Renovation",
      name: `${area.positioning} — ${area.name}`,
      provider: {
        "@type": "GeneralContractor",
        name: "Prime Build Design LTD",
        telephone: "+44 7481 626260",
        email: "primebuilddesignltd9@gmail.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "45 Bridge Road, Wembley Park",
          addressLocality: "London",
          postalCode: "HA9 9AG",
          addressCountry: "GB",
        },
      },
      areaServed: { "@type": "Place", name: `${area.name}, London (${area.postcode})` },
      description: area.intro,
    },
    buildBreadcrumbs([
      { name: "Areas", path: "/#areas" },
      { name: `Builders in ${area.name}`, path: `/areas/${slug}` },
    ]),
    faqJsonLd(area.faqs || []),
  ];

  // Internal cross-linking — pick 3 other areas
  const relatedAreaSlugs = AREA_SLUGS.filter((s) => s !== slug).slice(0, 3);

  return (
    <div data-testid={`area-page-${slug}`}>
      <Seo
        title={area.metaTitle || `Builders in ${area.name} (${area.postcode}) — ${area.positioning}`}
        description={area.metaDescription || `${area.positioning} in ${area.name} ${area.postcode}. ${area.intro.slice(0, 110)}…`}
        path={`/areas/${slug}`}
        image={area.heroImg || UK_HERO_IMG}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-30">
          <img src={area.heroImg || UK_HERO_IMG} alt={`${area.name} residential street — Prime Build Design`} className="w-full h-full object-cover" loading="eager" decoding="async" width="1600" height="900" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-6 text-[#C5A059] flex items-center gap-2">
            <MapPin size={14} /> {area.positioning} · {area.postcode}
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight leading-[1.02] max-w-4xl">
            Builders in<br /> {area.name}.
          </h1>
          <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">{area.intro}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-accent" data-testid={`area-${slug}-cta-quote`}>
              Request a free consultation <ArrowUpRight size={16} />
            </Link>
            <a href={PHONE_TEL} className="btn-ghost text-white border-white hover:bg-white hover:text-slate-900" data-testid={`area-${slug}-cta-call`}>
              <Phone size={16} /> Call 07481 626 260
            </a>
          </div>
        </div>
      </section>

      {/* Long-form narrative sections — the SEO body */}
      <section className="bg-white" data-testid={`area-${slug}-longform`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          {(area.sections || []).map((s, i) => (
            <article key={i} className={i === 0 ? "" : "mt-14 pt-14 border-t border-slate-200"} data-testid={`area-${slug}-section-${i}`}>
              <div className="overline text-[#C5A059] mb-3">Section · 0{i + 1}</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                {s.heading}
              </h2>
              <p className="mt-6 text-slate-700 leading-[1.9] text-lg">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Linked services — internal SEO links to Service pages */}
      {linkedServices.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200" data-testid={`area-${slug}-linked-services`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="overline mb-3">Popular services in {area.name}</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
              What we typically build in {area.postcode}.
            </h2>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
              {linkedServices.map((svc) => (
                <Link
                  key={svc.slug}
                  to={`/services/${svc.slug}`}
                  data-testid={`area-${slug}-service-${svc.slug}`}
                  className="bg-white p-8 hover:bg-slate-100 transition-colors group flex flex-col justify-between min-h-[220px]"
                >
                  <div>
                    <div className="overline text-[#C5A059]">Service</div>
                    <h3 className="mt-3 font-display text-xl font-semibold text-slate-900 leading-snug">
                      {svc.name}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                      {svc.intro}
                    </p>
                  </div>
                  <div className="text-sm font-medium mt-5 inline-flex items-center gap-1 text-slate-900 group-hover:text-[#C5A059] transition-colors">
                    Explore <ArrowUpRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local projects */}
      {localProjects.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="overline mb-4">Local projects</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-12">
              Recently delivered in {area.name}.
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {localProjects.slice(0, 6).map((p) => (
                <div key={p.id} className="zoom-img relative h-[360px] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/15 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="overline text-white/70">{p.category}</div>
                    <div className="font-display text-lg font-semibold mt-1">{p.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local FAQs */}
      {area.faqs && area.faqs.length > 0 && (
        <FaqSection
          faqs={area.faqs}
          overline={`FAQs · Builders in ${area.name}`}
          title={`Answers for ${area.name} homeowners.`}
          background="slate"
          testId={`area-${slug}-faq`}
        />
      )}

      {/* Google Maps */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="overline mb-4">Serving {area.name}</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
            Builders covering {area.name} {area.postcode}.
          </h2>
          <div className="border border-slate-200 overflow-hidden">
            <iframe
              title={`${area.name} coverage map`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(area.name + ', London, UK')}&output=embed`}
              width="100%" height="360" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <div className="font-display text-5xl text-[#C5A059]">"</div>
          <p className="mt-4 font-display text-2xl md:text-3xl font-light max-w-3xl mx-auto leading-relaxed">{area.testimonial}</p>
          <div className="mt-6 overline text-slate-400">{area.testimonialAuthor}</div>
        </div>
      </section>

      {/* Related areas — internal linking */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="overline mb-3">Also covering</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
            Builders across London.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-slate-200">
            {relatedAreaSlugs.map((rs) => {
              const r = AREAS[rs];
              return (
                <Link
                  key={rs}
                  to={`/areas/${rs}`}
                  data-testid={`area-${slug}-related-${rs}`}
                  className="bg-white p-8 hover:bg-slate-50 transition-colors group"
                >
                  <div className="overline text-[#C5A059]">{r.postcode}</div>
                  <div className="font-display text-2xl font-semibold text-slate-900 mt-3">
                    Builders in {r.name}
                  </div>
                  <div className="text-sm text-slate-600 mt-2">{r.positioning}</div>
                  <div className="text-sm font-medium mt-5 inline-flex items-center gap-1 text-slate-900 group-hover:text-[#C5A059] transition-colors">
                    Explore {r.name} <ArrowUpRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Planning a project in {area.name}?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link to="/contact" className="bg-slate-900 text-white px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition" data-testid={`area-${slug}-bottom-quote`}>
              Request a quote <ArrowUpRight size={18} />
            </Link>
            <a href={WA(`Hi Prime Build, I'd like a quote for a project in ${area.name}.`)} target="_blank" rel="noreferrer" className="bg-white text-slate-900 px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-50 transition" data-testid={`area-${slug}-bottom-wa`}>
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
